import { BaseModule } from './baseModule';
import type { AxiosInstance } from 'axios';
import type { ErrorHandler } from './errorHandler';
import type {
  JobAnalyticsResponse,
  JobStats,
  JobViewRequest,
  ApplicationAnalytics,
  MessageResponse,
} from './types';

/**
 * AnalyticsModule - Handles analytics and reporting operations
 * 
 * This module provides comprehensive analytics functionality including:
 * - Job analytics with view and application tracking
 * - Application analytics and conversion metrics
 * - Job view tracking
 * - Performance metrics and reporting
 */
export class AnalyticsModule extends BaseModule {
  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    super(httpClient, errorHandler);
  }

  // ============================================================================
  // Job Analytics
  // ============================================================================

  /**
   * Get comprehensive job analytics
   * GET /api/admin/jobs/analytics
   * 
   * @param params Optional filtering parameters
   * @param params.job_id Filter by specific job ID
   * @param params.start_date Filter by start date (ISO string)
   * @param params.end_date Filter by end date (ISO string)
   */
  async getJobAnalytics(params?: {
    job_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<JobAnalyticsResponse> {
    const queryString = params ? this.buildQueryString(params) : '';
    const endpoint = queryString 
      ? `/api/admin/jobs/analytics?${queryString}` 
      : '/api/admin/jobs/analytics';
    return this.get<JobAnalyticsResponse>(endpoint);
  }

  /**
   * Get detailed statistics for a specific job
   * GET /api/jobs/:id/stats
   * 
   * @param jobId The job ID to get stats for
   */
  async getJobStats(jobId: string): Promise<JobStats> {
    return this.get<JobStats>(`/api/jobs/${jobId}/stats`);
  }

  /**
   * Track a job view
   * POST /api/jobs/:id/view
   * 
   * @param jobId The job ID being viewed
   * @param data View tracking data (user agent, referrer, etc.)
   */
  async trackJobView(jobId: string, data: JobViewRequest): Promise<MessageResponse> {
    return this.post<MessageResponse>(`/api/jobs/${jobId}/view`, data);
  }

  // ============================================================================
  // Application Analytics
  // ============================================================================

  /**
   * Get application analytics and conversion metrics
   * GET /api/admin/applications/analytics
   * 
   * Provides comprehensive application analytics including:
   * - Total applications count
   * - Applications by status breakdown
   * - Applications by job
   * - Conversion rates through the hiring funnel
   * - Recent applications
   */
  async getApplicationAnalytics(): Promise<ApplicationAnalytics> {
    return this.get<ApplicationAnalytics>('/api/admin/applications/analytics');
  }

  // ============================================================================
  // Helper Methods for Analytics
  // ============================================================================

  /**
   * Get job analytics for a specific date range
   * 
   * @param startDate Start date for analytics period
   * @param endDate End date for analytics period
   */
  async getJobAnalyticsByDateRange(
    startDate: Date,
    endDate: Date
  ): Promise<JobAnalyticsResponse> {
    return this.getJobAnalytics({
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    });
  }

  /**
   * Get analytics for a specific job
   * 
   * @param jobId The job ID to get analytics for
   */
  async getJobAnalyticsById(jobId: string): Promise<JobAnalyticsResponse> {
    return this.getJobAnalytics({ job_id: jobId });
  }

  /**
   * Get analytics for a specific job within a date range
   * 
   * @param jobId The job ID to get analytics for
   * @param startDate Start date for analytics period
   * @param endDate End date for analytics period
   */
  async getJobAnalyticsByIdAndDateRange(
    jobId: string,
    startDate: Date,
    endDate: Date
  ): Promise<JobAnalyticsResponse> {
    return this.getJobAnalytics({
      job_id: jobId,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    });
  }

  /**
   * Track anonymous job view (without user agent/referrer)
   * 
   * @param jobId The job ID being viewed
   */
  async trackSimpleJobView(jobId: string): Promise<MessageResponse> {
    return this.trackJobView(jobId, {});
  }

  /**
   * Track job view with full tracking data
   * 
   * @param jobId The job ID being viewed
   * @param userAgent User agent string from browser
   * @param referrer Referrer URL
   */
  async trackDetailedJobView(
    jobId: string,
    userAgent?: string,
    referrer?: string
  ): Promise<MessageResponse> {
    return this.trackJobView(jobId, {
      user_agent: userAgent,
      referrer: referrer,
    });
  }
}
